<template>
  <!-- Mobile: compact zoom in/out buttons -->
  <div
    v-if="!isDesktop"
    class="flex h-10 items-center rounded-lg bg-white shadow-md"
    data-testid="pinning-zoom-controls"
  >
    <button
      type="button"
      class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-lg border-none bg-transparent p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="!canZoomOut"
      data-testid="pinning-zoom-out-button"
      @click="handleZoomOut"
    >
      <UseSvg icon="zoom-out-o" class="h-5 w-5" />
    </button>
    <div class="h-8 w-px bg-gray-100" />
    <button
      type="button"
      class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-r-lg border-none bg-transparent p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="!canZoomIn"
      data-testid="pinning-zoom-in-button"
      @click="handleZoomIn"
    >
      <UseSvg icon="zoom-in-o" class="h-5 w-5" />
    </button>
  </div>

  <!-- Desktop: zoom out / percentage / zoom in -->
  <div
    v-else
    class="flex h-10 items-center gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm"
    data-testid="pinning-zoom-controls"
  >
    <button
      type="button"
      class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="!canZoomOut"
      aria-label="Zoom out"
      data-testid="pinning-zoom-out-button"
      @click="handleZoomOut"
    >
      <UseSvg icon="minus" class="h-4 w-4" />
    </button>

    <div
      role="status"
      aria-label="Zoom percentage"
      class="flex h-8 w-14 select-none items-center justify-center rounded-md border border-gray-200 bg-white text-sm font-medium text-gray-700"
    >
      {{ zoomPercentage }}%
    </div>

    <button
      type="button"
      class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-none bg-transparent hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="!canZoomIn"
      aria-label="Zoom in"
      data-testid="pinning-zoom-in-button"
      @click="handleZoomIn"
    >
      <UseSvg icon="plus" class="h-4 w-4" />
    </button>
  </div>
</template>

<script setup>
import UseSvg from "common/vue/components/UseSvg.vue";
import { computed } from "vue";

defineOptions({ name: "PinningZoom" });

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 1;

const props = defineProps({
  zoom: { type: Number, required: true },
  maxZoom: { type: Number, default: 10 },
  isDesktop: { type: Boolean, default: false }
});

const emit = defineEmits(["update:zoom"]);

const zoomPercentage = computed(() => Math.round(props.zoom * 100));
const canZoomIn = computed(() => props.zoom < props.maxZoom);
const canZoomOut = computed(() => props.zoom > MIN_ZOOM);

function handleZoomIn() {
  const newZoom = Math.min(props.zoom + ZOOM_STEP, props.maxZoom);
  emit("update:zoom", newZoom);
}

function handleZoomOut() {
  const newZoom = Math.max(props.zoom - ZOOM_STEP, MIN_ZOOM);
  emit("update:zoom", newZoom);
}
</script>
